import React from 'react';
import { createApp } from '@backstage/frontend-defaults';
import {
  createFrontendModule,
  ApiBlueprint,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  configApiRef,
  oauthRequestApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import type {
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/frontend-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import techDocsPlugin from '@backstage/plugin-techdocs/alpha';
import { techDocsMermaidAddonModule } from 'backstage-plugin-techdocs-addon-mermaid';
import { SignInPage } from '@backstage/core-components';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { navModule } from './modules/nav';
import { iconsModule } from './modules/icons';
import { themeModule } from './modules/theme';
import { scaffolderFieldsModule } from './modules/scaffolderFields';

export const jumpcloudOidcAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({
  id: 'auth.oidc.jumpcloud',
});

const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ApiBlueprint.make({
      params: defineParams =>
        defineParams(
          createApiFactory({
            api: jumpcloudOidcAuthApiRef,
            deps: {
              discoveryApi: discoveryApiRef,
              oauthRequestApi: oauthRequestApiRef,
              configApi: configApiRef,
            },
            factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
              OAuth2.create({
                configApi,
                discoveryApi,
                oauthRequestApi,
                provider: {
                  id: 'oidc',
                  title: 'JumpCloud',
                  icon: () => null,
                },
                environment: configApi.getOptionalString('auth.environment'),
                defaultScopes: ['openid', 'profile', 'email', 'offline_access'],
                popupOptions: { size: { fullscreen: true } },
              }),
          }),
        ),
    }),
    SignInPageBlueprint.make({
      params: {
        loader: async () => function PivotreeSignInPage(props) {
          const configApi = useApi(configApiRef);
          const guestEnabled = Boolean(
            configApi.getOptionalBoolean('app.signIn.guestEnabled'),
          );
          const oidcProvider = {
            id: 'oidc-auth-provider',
            title: 'JumpCloud',
            message: 'Sign in with JumpCloud SSO',
            apiRef: jumpcloudOidcAuthApiRef,
          };
          const providers = guestEnabled
            ? (['guest', oidcProvider] as const)
            : ([oidcProvider] as const);
          return React.createElement(SignInPage, {
            ...props,
            providers: [...providers],
          });
        },
      },
    }),
  ],
});

export default createApp({
  features: [catalogPlugin, scaffolderPlugin, techDocsPlugin, techDocsMermaidAddonModule, navModule, authModule, iconsModule, themeModule, scaffolderFieldsModule],
});
